/**
 * Main Application Component
 *
 * Sets up:
 * - React Router
 * - Auth Context Provider
 * - Organization Context Provider
 * - React Query for data fetching with polling
 * - Error Boundary for catching component errors
 */

import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { queryClient } from './lib/queryClient';
import { router } from './router';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <OrganizationProvider>
            <RouterProvider router={router} />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                // Inverted per the retheme: toasts are graphite, square, and
                // unshadowed; a 3px left rail carries success/error meaning so
                // colour never has to fill the surface.
                style: {
                  background: '#17171A',
                  color: '#FFFFFF',
                  borderRadius: 0,
                  boxShadow: 'none',
                  borderLeft: '3px solid #3A3A3F',
                  padding: '12px 16px',
                  maxWidth: '420px',
                },
                success: {
                  style: { borderLeft: '3px solid #2FBF6B' },
                  iconTheme: { primary: '#2FBF6B', secondary: '#17171A' },
                },
                error: {
                  duration: 6000,
                  style: { borderLeft: '3px solid #E5342A' },
                  iconTheme: { primary: '#E5342A', secondary: '#17171A' },
                },
              }}
            />
          </OrganizationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
